import { MutableRefObject, RefObject, createContext, useContext } from 'react'

import { PointCloudOctree } from '@datalabs-jp/three-loader'
import { ArcballControls } from '@editor/services/threejs'
import { OrthographicCamera, PerspectiveCamera, Points, Scene } from 'three'

export interface BlueprintEditorContextState {
  activeCamera: MutableRefObject<PerspectiveCamera | OrthographicCamera>
  perspectiveCamera: MutableRefObject<PerspectiveCamera>
  orthographicCamera: MutableRefObject<OrthographicCamera>
  arcballControls: MutableRefObject<ArcballControls | null>

  sceneRef: MutableRefObject<Scene | null>

  pointClouds: PointCloudOctree[]
  setPointClouds: React.Dispatch<React.SetStateAction<PointCloudOctree[]>>
  pointCloud2ds: (Points | PointCloudOctree)[]
  setPointCloud2ds: React.Dispatch<React.SetStateAction<(Points | PointCloudOctree)[]>>

  /**
   * Flag to indicate if the user is currently adjusting an object.
   */
  isAdjustingRef: MutableRefObject<boolean>

  /**
   * Prevent exiting editing mode of an object when clicking outside of the object.
   */
  shouldPreventExitEditingMode: MutableRefObject<boolean>

  /**
   * To be used if `shouldPreventExitEditingMode` can't be toggled off late enough for checking function to run.
   * To be used with `checkPreventExitEditingMode()`, if this flag is true, it will return true to prevent exiting editing mode
   * but will reset both flags to false immediately after allowing subsequent calls to `checkPreventExitEditingMode()` to allow exiting editing mode.
   *
   * E.g. when using a popover, closing event of the popover runs first before the click event that triggered the closing.
   */
  shouldTogglePreventExitEditingMode: MutableRefObject<boolean>

  /**
   * Check if exiting editing mode should be prevented.
   */
  checkPreventExitEditingMode: () => boolean

  /**
   * Reset the prevent exit editing mode flag.
   */
  resetPreventExitEditingMode: () => void
}

const initialState: BlueprintEditorContextState = {
  perspectiveCamera: { current: new PerspectiveCamera() } as RefObject<PerspectiveCamera>,
  orthographicCamera: { current: new OrthographicCamera() } as RefObject<OrthographicCamera>,
  activeCamera: { current: new PerspectiveCamera() } as RefObject<PerspectiveCamera | OrthographicCamera>,
  arcballControls: { current: null } as RefObject<ArcballControls | null>,

  sceneRef: { current: null } as MutableRefObject<Scene | null>,

  pointClouds: [],
  setPointClouds: () => null,
  pointCloud2ds: [],
  setPointCloud2ds: () => null,

  isAdjustingRef: { current: false } as MutableRefObject<boolean>,

  shouldPreventExitEditingMode: { current: false } as MutableRefObject<boolean>,
  shouldTogglePreventExitEditingMode: { current: false } as MutableRefObject<boolean>,
  checkPreventExitEditingMode: () => false,
  resetPreventExitEditingMode: () => null,
} as BlueprintEditorContextState

export const BlueprintEditorContext = createContext(initialState)
export const useBlueprintEditorContext = () => useContext(BlueprintEditorContext)
